import { createFileRoute } from "@tanstack/react-router";
import { add, nextSunday } from "date-fns";
import { useCallback, useState } from "react";
import { z } from "zod";
import { $$articlesRecords, useArticlesQuery } from "@/api/queries";
import type { Timetypes } from "@/api/services";
import type { PlatformOptions } from "@/api/types";
import { ArticleStatusFilterDropdown } from "@/components/articles/article-status-filter-dropdown";
import { StoriesList } from "@/components/articles/articles-list";
import { CalendarWeekNav } from "@/components/articles/calendar-week-nav";
import { SectionSelect } from "@/components/articles/section-select";
import { StoryCalendar } from "@/components/articles/story-calendar";
import { Navbar, type NavbarView } from "@/components/navbar";
import { SearchBar } from "@/components/search-bar/search-bar";
import { type Option, SimpleSelect } from "@/components/simple-select";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { LocalStorage } from "@/lib/local-storage";
import { cn } from "@/lib/utils";

const formatDate = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

const getUpcomingSunday = (): Date => {
  const today = new Date();
  return nextSunday(today);
};

const getScheduleDateRangeFromTimetype = (currentTimetype?: Timetypes) => {
  const today = new Date();
  const todayStr = formatDate(today);

  switch (currentTimetype) {
    case "today":
      return { publish_at_after: todayStr, publish_at_before: todayStr };
    case "tomorrow": {
      const tomorrowStr = formatDate(add(today, { days: 1 }));
      return { publish_at_after: tomorrowStr, publish_at_before: tomorrowStr };
    }
    case "coming-week": {
      const weekFromNow = add(today, { weeks: 1 });
      return { publish_at_after: todayStr, publish_at_before: formatDate(weekFromNow) };
    }
    case "coming-sunday": {
      const sunday = getUpcomingSunday();
      const sundayStr = formatDate(sunday);
      return { publish_at_after: sundayStr, publish_at_before: sundayStr };
    }
    default:
      return { publish_at_after: undefined, publish_at_before: undefined };
  }
};

const getScheduleSelectorValue = (
  publish_before?: string,
  publish_after?: string,
  currentTimetype?: Timetypes,
): string => {
  if (
    currentTimetype === "today" ||
    currentTimetype === "tomorrow" ||
    currentTimetype === "coming-week" ||
    currentTimetype === "coming-sunday" ||
    currentTimetype === "specific-dates"
  ) {
    return currentTimetype;
  }

  if (publish_after || publish_before) {
    return "specific-dates";
  }

  return "any-time";
};

const articleQuerySchema = z.object({
  page: z.coerce.number().default(1).catch(1),
  query: z.string().default("").catch(""),
  limit: z.coerce.number().default(20).catch(20),
  timetype: z.custom<Timetypes>().optional().catch(undefined),
  publish_at_after: z
    .string()
    .optional()
    .refine((v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), { message: "date must be in YYYY-MM-DD format" })
    .catch(undefined),
  publish_at_before: z
    .string()
    .optional()
    .refine((v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), { message: "date must be in YYYY-MM-DD format" })
    .catch(undefined),

  platform: z.custom<PlatformOptions>().optional().catch(undefined),
  status: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .catch(undefined),
  section: z
    .union([z.coerce.number().transform((n) => [n]), z.array(z.coerce.number())])
    .optional()
    .catch(undefined),
  view: z.enum(["list", "calendar"]).default("list").catch("list"),
});

type StoriesFilters = z.infer<typeof articleQuerySchema>;

const STORY_VIEWS: readonly NavbarView[] = [
  { value: "list", label: "Stories" },
  { value: "calendar", label: "Schedule" },
];

// Story-list filters are persisted in localStorage (not the URL) so an editor's
// selection survives leaving and returning to the list. See OBS-1291.
const STORIES_FILTERS_KEY = "stories_filters";

const getStoredFilters = (): StoriesFilters =>
  LocalStorage.get(STORIES_FILTERS_KEY, articleQuerySchema) ?? articleQuerySchema.parse({});

export const Route = createFileRoute("/_auth-init-space/_authorized/stories/")({
  component: StoriesRoute,
  loader: ({ context: { queryClient } }) => {
    const filters = getStoredFilters();
    // The Schedule view reads its own queries, so a list prefetch would fire a request (and its
    // Wagtail fan-out) that nothing renders.
    if (filters.view === "calendar") return;
    queryClient.prefetchQuery(
      $$articlesRecords({
        page: filters.page,
        query: filters.query,
        limit: filters.limit,
        timetype: filters.timetype,
        publish_at_after: filters.publish_at_after,
        publish_at_before: filters.publish_at_before,
        status: filters.status,
        platform: filters.platform,
        section: filters.section,
      }),
    );
  },
});

function StoriesRoute() {
  const [filters, setFilters] = useState<StoriesFilters>(getStoredFilters);
  const {
    page,
    query,
    limit,
    publish_at_after,
    publish_at_before,
    timetype,
    status,
    platform,
    section,
    view,
  } = filters;
  const isCalendar = view === "calendar";
  const [weekOffset, setWeekOffset] = useState(0);

  const updateFilters = useCallback((changes: Partial<StoriesFilters>) => {
    setFilters((prev) => {
      const next = { ...prev, ...changes };
      LocalStorage.set(STORIES_FILTERS_KEY, next);
      return next;
    });
  }, []);

  const scheduleFilter = getScheduleSelectorValue(publish_at_before, publish_at_after, timetype);
  const showCustomDates = scheduleFilter === "specific-dates";

  const scheduleRange = getScheduleDateRangeFromTimetype(timetype);
  const filteredPublishAtAfter = scheduleRange.publish_at_after ?? publish_at_after;
  const filteredPublishAtBefore = scheduleRange.publish_at_before ?? publish_at_before;

  const { data, isLoading } = useArticlesQuery(
    {
      page,
      query,
      limit,
      status,
      publish_at_after: filteredPublishAtAfter,
      publish_at_before: filteredPublishAtBefore,
      platform,
      section,
    },
    !isCalendar,
  );
  const handlePageChange = (newPage: number) => {
    updateFilters({ page: newPage });
  };

  const handleSearch = (newQuery: string) => {
    updateFilters({ query: newQuery, page: 1 });
  };

  const scheduleOptions: Option[] = [
    { label: "Any time", value: "any-time" },
    { label: "Not scheduled", value: "not-scheduled" },
    { label: "Today", value: "today" },
    { label: "Tomorrow", value: "tomorrow" },
    { label: "Coming week", value: "coming-week" },
    { label: "Coming Sunday", value: "coming-sunday" },
    { label: "Recently published", value: "recently-published" },
    { label: "Specific dates...", value: "specific-dates" },
  ];

  const handleScheduleFilterChange = (filterValue: string) => {
    switch (filterValue) {
      case "any-time": {
        updateFilters({
          timetype: undefined,
          publish_at_after: undefined,
          publish_at_before: undefined,
          page: 1,
        });
        break;
      }

      case "not-scheduled": {
        updateFilters({
          status: "web-draft",
          timetype: undefined,
          publish_at_after: undefined,
          publish_at_before: undefined,
          page: 1,
        });
        break;
      }

      case "today": {
        updateFilters({
          timetype: "today",
          publish_at_after: undefined,
          publish_at_before: undefined,
          page: 1,
        });
        break;
      }

      case "tomorrow": {
        updateFilters({
          timetype: "tomorrow",
          publish_at_after: undefined,
          publish_at_before: undefined,
          page: 1,
        });
        break;
      }

      case "coming-week": {
        updateFilters({
          timetype: "coming-week",
          publish_at_after: undefined,
          publish_at_before: undefined,
          page: 1,
        });
        break;
      }

      case "coming-sunday": {
        updateFilters({
          timetype: "coming-sunday",
          publish_at_after: undefined,
          publish_at_before: undefined,
          page: 1,
        });
        break;
      }

      case "recently-published": {
        updateFilters({
          timetype: undefined,
          publish_at_after: undefined,
          publish_at_before: undefined,
          status: "web-live",
          page: 1,
        });
        break;
      }

      case "specific-dates": {
        updateFilters({
          timetype: "specific-dates",
          page: 1,
        });
        break;
      }

      default:
        break;
    }
  };

  const handleCustomScheduleDateChange = (
    newDate: string,
    dateField: "publish_at_after" | "publish_at_before",
  ) => {
    updateFilters({
      timetype: "specific-dates",
      [dateField]: newDate,
      page: 1,
    });
  };

  const handleSetStatus = (newStatus: string | string[] | undefined) => {
    updateFilters({ status: newStatus, page: 1 });
  };

  const handleSetSection = (newSectionIds: number[] | undefined) => {
    updateFilters({ section: newSectionIds, page: 1 });
  };

  const hasActiveFilters = !!(
    query ||
    timetype ||
    publish_at_after ||
    publish_at_before ||
    status ||
    section?.length
  );

  const handleClearFilters = () => {
    updateFilters({
      query: "",
      timetype: undefined,
      publish_at_after: undefined,
      publish_at_before: undefined,
      status: undefined,
      section: undefined,
      page: 1,
    });
  };

  return (
    <div className="flex flex-col flex-1">
      <Navbar
        views={STORY_VIEWS}
        activeView={view}
        onViewChange={(next) => updateFilters({ view: next as StoriesFilters["view"] })}
        center={
          isCalendar ? (
            <CalendarWeekNav weekOffset={weekOffset} onWeekOffsetChange={setWeekOffset} />
          ) : undefined
        }
      />
      <div className="flex flex-1 justify-center items-start mt-(--navbar-height)">
        <div className={cn("flex-1 min-w-0", !isCalendar && "pb-6")}>
          {isCalendar ? (
            <StoryCalendar weekOffset={weekOffset} />
          ) : (
            <StoriesList
              stories={data?.results ?? []}
              isLoading={isLoading}
              page={page}
              hasNext={!!data?.next}
              hasPrevious={!!data?.previous}
              totalCount={data?.count ?? 10}
              pageSize={limit}
              onPageChange={handlePageChange}
            />
          )}
        </div>
        {/* The calendar defines its own date and status axes and spans the full width, so the
            filter rail has nothing left to say in that view. */}
        {!isCalendar && (
          <div className="self-stretch relative pr-6 grow-0 shrink-0 basis-[267px] border-l-border border-l pl-6">
            <div className="sticky top-(--navbar-height)">
              <SearchBar searchQuery={query} onQueryChange={handleSearch} />
              <div className="flex flex-col gap-4">
                <Field>
                  <FieldLabel className="font-light">Scheduled</FieldLabel>
                  <SimpleSelect
                    value={scheduleFilter}
                    onChange={handleScheduleFilterChange}
                    options={scheduleOptions}
                  />
                </Field>
                {showCustomDates && (
                  <>
                    <Field>
                      <FieldLabel className="font-light">Scheduled from</FieldLabel>
                      <Input
                        type="date"
                        value={publish_at_after || ""}
                        onChange={(e) => handleCustomScheduleDateChange(e.target.value, "publish_at_after")}
                      />
                    </Field>
                    <Field>
                      <FieldLabel className="font-light">Scheduled to</FieldLabel>
                      <Input
                        type="date"
                        value={publish_at_before || ""}
                        onChange={(e) => handleCustomScheduleDateChange(e.target.value, "publish_at_before")}
                      />
                    </Field>
                  </>
                )}
                <Field>
                  <FieldLabel className="font-light">Status</FieldLabel>
                  <ArticleStatusFilterDropdown value={status} onStatusChange={handleSetStatus} />
                </Field>

                <Field>
                  <FieldLabel className="font-light">Section</FieldLabel>
                  <SectionSelect value={section} onChange={handleSetSection} />
                </Field>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="text-xs underline text-center text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
